import axios from 'axios'

import { Address } from './AddressLookup'
import { USTerritories } from './geo'

type MapboxResponse = {
  type: string
  query: string[]
  features: MapboxFeature[]
}

type MapboxFeature = {
  context: MapboxContext[]
  center?: number[]
  address?: string
  text?: string
}

type MapboxContext = {
  id: string
  text: string
  wikidata?: string
  short_code?: string
}

type MapboxRequest = {
  access_token: string
  country: string
  types: string
  proximity?: string
}

export type Location = {
  latitude: number
  longitude: number
}

const getBy = (context: MapboxContext[], id: string) => {
  const item = context?.find((item: any) => item.id.split('.')[0] === id)
  return item
}

const convertMapboxFeatureToAddress = (
  value: MapboxFeature,
  includeStateName?: boolean
): Address => {
  const countryContext = getBy(value?.context, 'country')
  let country =
    countryContext && countryContext['short_code']
      ? countryContext['short_code']?.toUpperCase()
      : 'US'
  const stateContext = getBy(value?.context, 'region')
  const stateName = stateContext?.text
  let stateCode = stateContext?.short_code?.split('-')[1]
  const isUSTerritory =
    countryContext &&
    USTerritories.includes(
      countryContext['short_code']?.toUpperCase() as string
    )
  if (isUSTerritory) {
    country = 'US'
    stateCode = countryContext['short_code']?.toUpperCase()
  }

  const cityContext =
    getBy(value?.context, 'locality') || getBy(value?.context, 'place')
  const city = cityContext?.text
  const zipCodeContext = getBy(value?.context, 'postcode')
  const zipCode = zipCodeContext?.text

  const result: Address = {
    address1: value?.address ? `${value?.address} ${value?.text}` : value?.text,
    address2: '',
    city,
    stateCode,
    zipCode,
    country,
    latitude: value?.center && value.center[1],
    longitude: value?.center && value.center[0]
  }
  if (includeStateName) {
    result.stateName = stateName
  }
  return result
}

export const getMapboxSearch = async (
  search: string,
  mapboxAccessToken: string,
  countries: string,
  includeStateName?: boolean,
  proximity?: Location,
  includePOI?: boolean
) => {
  const searchTerm = encodeURIComponent(search)
  if (searchTerm === '') {
    return []
  }

  const params: MapboxRequest = {
    access_token: mapboxAccessToken,
    types: `address${includePOI ? ',poi' : ''}`,
    country: countries
  }

  if (proximity) {
    params.proximity = `${proximity.longitude},${proximity.latitude}`
  }

  const paramsStr = new URLSearchParams(params).toString()

  const { data } = await axios.get<MapboxResponse>(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${searchTerm}.json?${paramsStr}`
  )
  return data?.features?.map<Address>((feature: MapboxFeature) => {
    return convertMapboxFeatureToAddress(feature, includeStateName)
  })
}
